import type {
  Enabler,
  EnablerGroup,
  LearningCatalog,
  Objective,
  ObjectiveGroup,
  Resource,
  ResourceCategory
} from "../types";

const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "how",
  "in",
  "into",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "their",
  "this",
  "to",
  "with",
  "your"
]);

const SUGGESTED_RESOURCE_MIN_RECOMMENDATIONS = 3;
export const DEFAULT_RESOURCE_CATEGORY: ResourceCategory = "Learning materials";
export const RESOURCE_CATEGORIES: ResourceCategory[] = [
  "Learning materials",
  "Webinars",
  "Workshops",
  "Consultant Requests",
  "Network"
];

type ParsedQuery = {
  normalized: string;
  terms: string[];
  rareTerms: Set<string>;
};

type ResourceIndexes = {
  objectivesById: Map<string, Objective>;
  enablersByCode: Map<string, Enabler>;
};

type Filters = {
  searchTerm: string;
  selectedCategory: ResourceCategory;
  selectedStage: string | null;
  selectedObjective: string | null;
  selectedFormat: string | null;
  selectedEnablerTheme: string | null;
  selectedEnabler: string | null;
};

type ScoredResource = Resource & {
  score: number;
  originalIndex: number;
  matchedTermCount: number;
  phraseMatch: boolean;
  matchedRareTerm: boolean;
  coverageRatio: number;
};

export function getStages(catalog: LearningCatalog): string[] {
  return Array.from(new Set(catalog.objectives.map((objective) => objective.stage).filter(Boolean)));
}

export function getFormats(catalog: LearningCatalog, category: ResourceCategory = DEFAULT_RESOURCE_CATEGORY): string[] {
  return Array.from(
    new Set(
      catalog.resources
        .filter((resource) => getResourceCategory(resource) === category)
        .map((resource) => resource.format as string)
        .filter((format) => Boolean(format) && format !== "Webinar")
    )
  ).sort((a, b) => a.localeCompare(b));
}

export function getEnablerThemes(enablers: Enabler[]): string[] {
  return Array.from(new Set(enablers.map((enabler) => enabler.theme).filter(Boolean)));
}

export function createResourceIndexes(catalog: LearningCatalog): ResourceIndexes {
  return {
    objectivesById: new Map(catalog.objectives.map((objective) => [objective.id, objective])),
    enablersByCode: new Map(catalog.enablers.map((enabler) => [enabler.code, enabler]))
  };
}

export function filterResources(catalog: LearningCatalog, filters: Filters): Resource[] {
  const indexes = createResourceIndexes(catalog);
  const query = parseSearchQuery(filters.searchTerm, catalog.resources);

  return catalog.resources
    .map((resource, index) => scoreResource(resource, query, indexes, index))
    .filter((resource) => resourceMatchesFilters(resource, filters, indexes))
    .filter((resource) => resourcePassesSearchGate(resource, query))
    .sort((a, b) => compareBySearchRelevance(a, b, query))
    .map(({ score, originalIndex, matchedTermCount, phraseMatch, matchedRareTerm, coverageRatio, ...resource }) => resource);
}

function compareBySearchRelevance(a: ScoredResource, b: ScoredResource, query: ParsedQuery): number {
  if (!query.normalized) {
    return a.originalIndex - b.originalIndex;
  }

  return b.score - a.score || a.originalIndex - b.originalIndex;
}

export function getObjectiveGroups(catalog: LearningCatalog, filters: Filters): ObjectiveGroup[] {
  const filteredResources = filterResources(catalog, filters);
  const resourcesByObjective = groupResourcesById(filteredResources, "objectiveIds");

  return catalog.objectives
    .filter((objective) => (filters.selectedStage ? objective.stage === filters.selectedStage : true))
    .filter((objective) => (filters.selectedObjective ? objective.id === filters.selectedObjective : true))
    .map((objective) => {
      const resources = sortResourcesForObjective(resourcesByObjective.get(objective.id) ?? [], objective.code);
      return {
        ...objective,
        resources,
        topRecommendedResources: getTopRecommendedResourcesForObjective(resources, objective.code)
      };
    })
    .filter((objective) => objective.resources.length > 0 || !hasActiveResourceFilters(filters));
}

export function getEnablerGroups(catalog: LearningCatalog, filters: Filters): EnablerGroup[] {
  const filteredResources = filterResources(catalog, filters);
  const resourcesByEnabler = groupResourcesById(filteredResources, "enablerIds");

  return catalog.enablers
    .filter((enabler) => (filters.selectedEnablerTheme ? enabler.theme === filters.selectedEnablerTheme : true))
    .filter((enabler) => (filters.selectedEnabler ? enabler.code === filters.selectedEnabler : true))
    .map((enabler) => ({
      ...enabler,
      resources: resourcesByEnabler.get(enabler.code) ?? []
    }))
    .filter((enabler) => enabler.resources.length > 0 || !hasActiveResourceFilters(filters));
}

export function countUniqueResources(groups: Array<{ resources: Resource[] }>): number {
  return new Set(groups.flatMap((group) => group.resources.map((resource) => resource.id))).size;
}

function groupResourcesById(resources: Resource[], field: "objectiveIds" | "enablerIds"): Map<string, Resource[]> {
  const output = new Map<string, Resource[]>();

  for (const resource of resources) {
    for (const id of resource[field]) {
      const existing = output.get(id) ?? [];
      existing.push(resource);
      output.set(id, existing);
    }
  }

  return output;
}

function hasActiveResourceFilters(filters: Filters): boolean {
  return Boolean(
    hasSearchTerms(filters.searchTerm) ||
      filters.selectedCategory !== DEFAULT_RESOURCE_CATEGORY ||
      filters.selectedObjective ||
      filters.selectedFormat ||
      filters.selectedEnablerTheme ||
      filters.selectedEnabler
  );
}

function resourceMatchesFilters(resource: ScoredResource, filters: Filters, indexes: ResourceIndexes): boolean {
  if (getResourceCategory(resource) !== filters.selectedCategory) {
    return false;
  }

  if (filters.selectedFormat && resource.format !== filters.selectedFormat) {
    return false;
  }

  if (filters.selectedObjective && !resource.objectiveIds.includes(filters.selectedObjective)) {
    return false;
  }

  if (filters.selectedEnabler && !resource.enablerIds.includes(filters.selectedEnabler)) {
    return false;
  }

  if (filters.selectedEnablerTheme) {
    const hasThemeMatch = resource.enablerIds.some(
      (enablerId) => indexes.enablersByCode.get(enablerId)?.theme === filters.selectedEnablerTheme
    );
    if (!hasThemeMatch) {
      return false;
    }
  }

  if (filters.selectedStage) {
    return resource.objectiveIds.some((objectiveId) => indexes.objectivesById.get(objectiveId)?.stage === filters.selectedStage);
  }

  return true;
}

function getResourceCategory(resource: Resource): ResourceCategory {
  return resource.category ?? DEFAULT_RESOURCE_CATEGORY;
}

export function getRecommendationCount(resource: Resource): number {
  return resource.recommendationCount ?? 0;
}

export function isSuggestedResource(resource: Resource): boolean {
  return getRecommendationCount(resource) >= SUGGESTED_RESOURCE_MIN_RECOMMENDATIONS;
}

export function getObjectiveRecommendationCount(resource: Resource, objectiveCode: string): number {
  return resource.recommendationCountsByObjective?.[objectiveCode] ?? 0;
}

function getTopRecommendedResourcesForObjective(resources: Resource[], objectiveCode: string): Resource[] {
  return resources
    .filter((resource) => getObjectiveRecommendationCount(resource, objectiveCode) >= SUGGESTED_RESOURCE_MIN_RECOMMENDATIONS)
    .slice(0, 3);
}

function sortResourcesForObjective(resources: Resource[], objectiveCode: string): Resource[] {
  return [...resources].sort(
    (a, b) =>
      getObjectiveRecommendationCount(b, objectiveCode) - getObjectiveRecommendationCount(a, objectiveCode) ||
      getRecommendationCount(b) - getRecommendationCount(a) ||
      a.name.localeCompare(b.name)
  );
}

function parseSearchQuery(value: string, resources: Resource[] = []): ParsedQuery {
  const normalized = normalizeSearchText(value);
  const terms = normalized.split(" ").filter((term) => term.length > 0 && !SEARCH_STOP_WORDS.has(term));
  const uniqueTerms = Array.from(new Set(terms));

  return {
    normalized: uniqueTerms.join(" "),
    terms: uniqueTerms,
    rareTerms: getRareTerms(uniqueTerms, resources)
  };
}

function scoreResource(resource: Resource, query: ParsedQuery, _indexes: ResourceIndexes, originalIndex: number): ScoredResource {
  if (!query.normalized) {
    return {
      ...resource,
      score: 1,
      originalIndex,
      matchedTermCount: 0,
      phraseMatch: false,
      matchedRareTerm: false,
      coverageRatio: 0
    };
  }

  const titleText = normalizeSearchText(resource.name);
  const summaryText = normalizeSearchText(resource.summary ?? "");
  const searchableText = `${titleText} ${summaryText}`.trim();
  const matchedTerms = query.terms.filter((term) => searchableText.includes(term));
  const phraseMatch = searchableText.includes(query.normalized);
  const matchedRareTerm = matchedTerms.some((term) => query.rareTerms.has(term));
  const coverageRatio = matchedTerms.length / query.terms.length;
  const resourceScore =
    scoreField(resource.name, query, 260, 34) +
    scoreField(resource.summary ?? "", query, 110, 14) +
    (phraseMatch ? 90 : 0) +
    (matchedRareTerm ? 35 : 0) +
    coverageRatio * 40;

  return {
    ...resource,
    score: resourceScore,
    originalIndex,
    matchedTermCount: matchedTerms.length,
    phraseMatch,
    matchedRareTerm,
    coverageRatio
  };
}

function resourcePassesSearchGate(resource: ScoredResource, query: ParsedQuery): boolean {
  if (!query.normalized) {
    return true;
  }

  if (query.terms.length === 1) {
    return resource.matchedTermCount === 1;
  }

  if (query.terms.length === 2) {
    return resource.phraseMatch || resource.matchedTermCount === 2;
  }

  return resource.phraseMatch || resource.matchedTermCount === query.terms.length || (resource.matchedTermCount >= 2 && resource.matchedRareTerm);
}

function scoreField(value: string, query: ParsedQuery, phraseWeight: number, termWeight: number): number {
  const normalizedValue = normalizeSearchText(value);
  if (!normalizedValue) {
    return 0;
  }

  let score = normalizedValue.includes(query.normalized) ? phraseWeight : 0;

  for (const term of query.terms) {
    if (normalizedValue.includes(term)) {
      score += termWeight;
    }
  }

  return score;
}

export function getSearchHighlightTerms(value: string): string[] {
  const query = parseSearchQuery(value);

  if (!query.normalized) {
    return [];
  }

  return query.terms.filter((term) => term.length > 1);
}

export function hasSearchTerms(value: string): boolean {
  return parseSearchQuery(value).terms.length > 0;
}

function getRareTerms(terms: string[], resources: Resource[]): Set<string> {
  if (resources.length === 0 || terms.length === 0) {
    return new Set(terms);
  }

  const documentFrequency = new Map<string, number>();

  for (const resource of resources) {
    const resourceTerms = new Set(normalizeSearchText(`${resource.name} ${resource.summary ?? ""}`).split(" ").filter(Boolean));

    for (const term of terms) {
      if (resourceTerms.has(term)) {
        documentFrequency.set(term, (documentFrequency.get(term) ?? 0) + 1);
      }
    }
  }

  const rarityCutoff = resources.length * 0.15;
  const rareTerms = terms.filter((term) => (documentFrequency.get(term) ?? 0) <= rarityCutoff);
  return new Set(rareTerms.length > 0 ? rareTerms : terms);
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^a-z0-9.]+/g, " ")
    .split(" ")
    .map(normalizeSearchTerm)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSearchTerm(term: string): string {
  return term
    .replace(/^retention$/, "retain")
    .replace(/^retaining$/, "retain")
    .replace(/^retained$/, "retain")
    .replace(/ization\b/g, "isation")
    .replace(/izations\b/g, "isations")
    .replace(/izing\b/g, "ising")
    .replace(/ized\b/g, "ised")
    .replace(/ize\b/g, "ise")
    .replace(/yze\b/g, "yse");
}
